import React, { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  BarChart3,
  Table as TableIcon,
  Download,
  Camera,
  Maximize2,
  Sparkles,
} from "lucide-react";
import chartRegistry from "./ChartRegistry";
import TableAdapter from "./adapters/TableAdapter";
import "./AssistantChartCard.css";

export function escapeCSVCell(val) {
  if (val === null || val === undefined) return '""';
  if (typeof val === "number") {
    return String(val);
  }
  let str = String(val);
  const isNumericString = !isNaN(Number(str)) && str.trim() !== "";
  // Prefix formula-triggering characters with an apostrophe to prevent CSV formula injection
  if (!isNumericString && /^[\s]*[=+\-@\t\r]/.test(str)) {
    str = `'${str}`;
  }
  return `"${str.replace(/"/g, '""')}"`;
}

export function convertToCSV(data, config) {
  if (!data || !data.length) return "";
  const columns =
    config?.columns ||
    Object.keys(data[0]).map((key) => ({ key, label: key }));

  const headerRow = columns.map((col) => escapeCSVCell(col.label || col.key)).join(",");
  const bodyRows = data.map((row) =>
    columns
      .map((col) => escapeCSVCell(row[col.key]))
      .join(",")
  );

  return [headerRow, ...bodyRows].join("\n");
}

export function AssistantChartCard({ spec }) {
  const { t } = useTranslation();
  const [isTableView, setIsTableView] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const chartContainerRef = useRef(null);

  const validSpec = spec && typeof spec === "object" ? spec : null;
  const {
    type = "table",
    title = "",
    subtitle = "",
    data = [],
    config = {},
  } = validSpec || {};

  // Extract effective data rows from root envelope, nested spec, or Vega named datasets
  const effectiveData = React.useMemo(() => {
    if (Array.isArray(data) && data.length > 0) return data;
    const targetSpec = validSpec?.spec || validSpec;
    if (Array.isArray(targetSpec?.data?.values)) return targetSpec.data.values;
    if (
      Array.isArray(targetSpec?.data) &&
      targetSpec.data.length > 0 &&
      Array.isArray(targetSpec.data[0]?.values)
    ) {
      return targetSpec.data[0].values;
    }
    return Array.isArray(data) ? data : [];
  }, [data, validSpec]);

  const AdapterComponent = isTableView ? TableAdapter : chartRegistry.get(type);

  const handleExportCSV = useCallback(() => {
    if (!effectiveData.length) return;
    const csvContent = convertToCSV(effectiveData, config);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const safeTitle = (title || "geo-chart-data")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    link.setAttribute("href", url);
    link.setAttribute("download", `${safeTitle}-${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [effectiveData, config, title]);

  const handleExportImage = useCallback(() => {
    const container = chartContainerRef.current;
    if (!container) return;
    const canvas = container.querySelector("canvas");
    if (!canvas) {
      handleExportCSV();
      return;
    }

    try {
      const dataUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      const safeTitle = (title || "geo-chart-image")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
      link.setAttribute("href", dataUrl);
      link.setAttribute("download", `${safeTitle}-${Date.now()}.png`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error("Failed to export chart image:", e);
    }
  }, [title, handleExportCSV]);

  if (!validSpec) {
    return null;
  }

  return (
    <>
      <div className="assistant-chart-card" data-testid="assistant-chart-card">
        {/* Card Header */}
        <div className="assistant-chart-header">
          <div className="assistant-chart-title-group">
            <div className="assistant-chart-badge">
              <Sparkles size={13} className="assistant-chart-badge-icon" />
              <span>GEO Chart</span>
            </div>
            {title && <h5 className="assistant-chart-title">{title}</h5>}
            {subtitle && <span className="assistant-chart-subtitle">{subtitle}</span>}
          </div>

          {/* Action Toolbar */}
          <div className="assistant-chart-toolbar">
            <button
              type="button"
              className={`assistant-chart-tool-btn ${!isTableView ? "active" : ""}`}
              onClick={() => setIsTableView(false)}
              title={t("assistant.viewChart", "View Chart")}
              aria-label={t("assistant.viewChart", "View Chart")}
              data-testid="chart-view-toggle-chart"
            >
              <BarChart3 size={15} />
            </button>
            <button
              type="button"
              className={`assistant-chart-tool-btn ${isTableView ? "active" : ""}`}
              onClick={() => setIsTableView(true)}
              title={t("assistant.viewTable", "View Table")}
              aria-label={t("assistant.viewTable", "View Table")}
              data-testid="chart-view-toggle-table"
            >
              <TableIcon size={15} />
            </button>
            <div className="assistant-chart-toolbar-divider" />
            <button
              type="button"
              className="assistant-chart-tool-btn"
              onClick={handleExportCSV}
              title={t("assistant.exportCsv", "Export CSV")}
              aria-label={t("assistant.exportCsv", "Export CSV")}
              data-testid="chart-export-csv-btn"
            >
              <Download size={15} />
            </button>
            <button
              type="button"
              className="assistant-chart-tool-btn"
              onClick={handleExportImage}
              title={t("assistant.exportImage", "Export PNG Image")}
              aria-label={t("assistant.exportImage", "Export PNG Image")}
              data-testid="chart-export-png-btn"
            >
              <Camera size={15} />
            </button>
            <button
              type="button"
              className="assistant-chart-tool-btn"
              onClick={() => setIsExpanded(true)}
              title={t("assistant.expandChart", "Expand Fullscreen")}
              aria-label={t("assistant.expandChart", "Expand Fullscreen")}
              data-testid="chart-expand-btn"
            >
              <Maximize2 size={15} />
            </button>
          </div>
        </div>

        {/* Card Body */}
        <div className="assistant-chart-body">
          <AdapterComponent
            spec={isTableView ? { ...validSpec, data: effectiveData } : spec}
            containerRef={chartContainerRef}
          />
        </div>
      </div>

      {/* Expanded Modal */}
      {isExpanded && (
        <div className="assistant-chart-modal-backdrop" onClick={() => setIsExpanded(false)}>
          <div
            className="assistant-chart-modal-dialog"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="assistant-chart-modal-header">
              <div>
                <h4 className="assistant-chart-title mb-1">{title || "GEO Chart"}</h4>
                {subtitle && <span className="assistant-chart-subtitle">{subtitle}</span>}
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleExportCSV}
                >
                  <Download size={14} className="me-1" /> CSV
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleExportImage}
                >
                  <Camera size={14} className="me-1" /> PNG
                </button>
                <button
                  type="button"
                  className="btn-close ms-2"
                  onClick={() => setIsExpanded(false)}
                  aria-label="Close"
                />
              </div>
            </div>
            <div className="assistant-chart-modal-body">
              <AdapterComponent
                spec={
                  isTableView
                    ? { ...validSpec, data: effectiveData }
                    : { ...spec, config: { ...config, height: 450 } }
                }
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default React.memo(AssistantChartCard, (prevProps, nextProps) => {
  if (prevProps.spec === nextProps.spec) return true;
  try {
    return JSON.stringify(prevProps.spec) === JSON.stringify(nextProps.spec);
  } catch (e) {
    return false;
  }
});
